/** Small, dependency-free formatters. Numbers here are engineering numbers. */

/**
 * Render a plan limit.
 *
 * The API sends `-1` as the sentinel for "unlimited" (`environmentsAllowed`,
 * `runHistoryDays`). Printing that raw showed users `1 / -1` and `-1 days`,
 * which reads as a bug, and every Pro and Enterprise customer saw it.
 */
export function formatLimit(
  value: number | 'unlimited' | null | undefined,
  noun: 'day' | 'days' | 'environment' | 'environments',
): string {
  if (value === 'unlimited' || value === -1) return 'unlimited';
  if (value === null || value === undefined || Number.isNaN(value)) return '-';
  return `${value} ${value === 1 ? noun : noun.replace(/s$/, '')}`;
}

export function formatDuration(ms: number | null | undefined): string {
  if (ms === null || ms === undefined || Number.isNaN(ms)) return '-';
  if (ms < 1000) return `${Math.round(ms)}ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(ms < 10_000 ? 2 : 1)}s`;
  const minutes = Math.floor(ms / 60_000);
  const seconds = Math.round((ms % 60_000) / 1000);
  return `${minutes}m ${String(seconds).padStart(2, '0')}s`;
}

export function formatPercent(rate: number | null | undefined, digits = 0): string {
  if (rate === null || rate === undefined || Number.isNaN(rate)) return '-';
  return `${(rate * 100).toFixed(digits)}%`;
}

export function formatBytes(bytes: number | null | undefined): string {
  if (bytes === null || bytes === undefined || Number.isNaN(bytes)) return '-';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function formatTimestamp(iso: string | null | undefined): string {
  if (!iso) return '-';
  const parsed = Date.parse(iso);
  if (Number.isNaN(parsed)) return iso;
  const date = new Date(parsed);
  const pad = (value: number): string => String(value).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(
    date.getHours(),
  )}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
}

export function formatRelative(iso: string | null | undefined): string {
  if (!iso) return '-';
  const parsed = Date.parse(iso);
  if (Number.isNaN(parsed)) return iso;
  const deltaSeconds = Math.round((Date.now() - parsed) / 1000);
  if (deltaSeconds < 5) return 'just now';
  if (deltaSeconds < 60) return `${deltaSeconds}s ago`;
  if (deltaSeconds < 3600) return `${Math.floor(deltaSeconds / 60)}m ago`;
  if (deltaSeconds < 86_400) return `${Math.floor(deltaSeconds / 3600)}h ago`;
  if (deltaSeconds < 7 * 86_400) return `${Math.floor(deltaSeconds / 86_400)}d ago`;
  return formatTimestamp(iso);
}

/** "in 12m" for an expiry that has not happened yet. */
export function formatUntil(iso: string | null | undefined): string {
  if (!iso) return '-';
  const parsed = Date.parse(iso);
  if (Number.isNaN(parsed)) return iso;
  const seconds = Math.round((parsed - Date.now()) / 1000);
  if (seconds <= 0) return 'expired';
  if (seconds < 60) return `in ${seconds}s`;
  if (seconds < 3600) return `in ${Math.floor(seconds / 60)}m`;
  return `in ${Math.floor(seconds / 3600)}h ${Math.floor((seconds % 3600) / 60)}m`;
}

/** Short id for dense tables: full ids stay in the `title` attribute. */
export function shortId(id: string | null | undefined): string {
  if (!id) return '-';
  return id.length <= 12 ? id : `${id.slice(0, 10)}…`;
}

export function pluralize(count: number, singular: string, plural?: string): string {
  return `${count} ${count === 1 ? singular : (plural ?? `${singular}s`)}`;
}

export function cronHint(cron: string): string {
  // A nudge, not a parser: the server renders the authoritative sentence.
  const fields = cron.trim().split(/\s+/);
  return fields.length === 5 ? '5-field cron, server time (UTC)' : 'expected 5 cron fields';
}